import { Link } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/brand/logo';
import { currentLocale, setLocale } from '@/lib/i18n';

/* ---------------------------------------------------------------------------
   דפים ציבוריים: דף נחיתה, אודות, פרטיות ותנאי שימוש. Google דורשת אותם לאימות
   אפליקציית ה-OAuth — על הדומיין המאומת ובלי התחברות. מוצגים בשפת הממשק
   הנוכחית, ובכותרת יש מעבר שפה (הבודקים ב-Google קוראים אנגלית).
   ראו docs/google-oauth-verification.md.

   הטקסט ב-locales/{he,en}/public.json תחת legal.* ו-landing.*. בתוך הטקסט
   מותרים רק התגים שב-rich() למטה.

   התוכן מתאר את מה שהקוד עושה בפועל. שינוי בהרשאות (google-oauth.service.ts),
   בספקים או באיסוף מידע מחייב עדכון כאן — ו-Google משווה בין השניים.
   --------------------------------------------------------------------------- */

/** פרטי המפעיל ליצירת קשר. חייבים להיות אמיתיים ופעילים לפני ההגשה ל-Google. */
export const LEGAL = {
  product: 'CraftMind AI',
  operator: 'CraftMind AI',
  contactEmail: 'support@craftmind-ai.com',
  /** בקשות פרטיות: עיון, תיקון, מחיקה. מופיעה במדיניות הפרטיות. */
  privacyEmail: 'privacy@craftmind-ai.com',
  domain: 'craftmind-ai.com',
  updated: '08.10.2026',
  updatedEn: 'October 8, 2026',
};

const LIMITED_USE_URL = 'https://developers.google.com/terms/api-services-user-data-policy';

export const VALUES = {
  product: LEGAL.product,
  operator: LEGAL.operator,
  email: LEGAL.contactEmail,
  privacyEmail: LEGAL.privacyEmail,
  domain: LEGAL.domain,
};

/**
 * טקסט עם תגים פשוטים → React. רק התגים שכאן; כל תג אחר מוצג כטקסט.
 * (לא dangerouslySetInnerHTML — הטקסט מקובץ התרגום, אבל עדיף לא לסמוך על HTML.)
 */
export function rich(text: string): ReactNode {
  const parts: ReactNode[] = [];
  const re = /<(b|privacy|terms|limitedUse|mail|privacyMail|googlePerms)>(.*?)<\/\1>/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let n = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const inner = m[2]!;
    const key = n++;
    switch (m[1]) {
      case 'b':
        parts.push(<b key={key}>{inner}</b>);
        break;
      case 'privacy':
        parts.push(
          <Link key={key} to="/privacy" className="text-accent hover:underline">
            {inner}
          </Link>,
        );
        break;
      case 'terms':
        parts.push(
          <Link key={key} to="/terms" className="text-accent hover:underline">
            {inner}
          </Link>,
        );
        break;
      case 'limitedUse':
        parts.push(
          <a
            key={key}
            href={LIMITED_USE_URL}
            className="text-accent hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            {inner}
          </a>,
        );
        break;
      case 'mail':
        parts.push(
          <a
            key={key}
            href={`mailto:${LEGAL.contactEmail}`}
            className="text-accent hover:underline"
            dir="ltr"
          >
            {inner}
          </a>,
        );
        break;
      case 'privacyMail':
        parts.push(
          <a
            key={key}
            href={`mailto:${LEGAL.privacyEmail}`}
            className="text-accent hover:underline"
            dir="ltr"
          >
            {inner}
          </a>,
        );
        break;
      case 'googlePerms':
        parts.push(
          <a
            key={key}
            href="https://myaccount.google.com/permissions"
            className="text-accent hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            {inner}
          </a>,
        );
        break;
    }
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

/**
 * מבקר ראשון (בלי שפה שמורה) שהדפדפן שלו לא בעברית — אנגלית. הבודקים של Google
 * (אימות OAuth) קוראים אנגלית. רק בדפי אודות, פרטיות ותנאים (`enabled`); דף
 * המכירה נשאר בעברית כברירת מחדל, ומעבר השפה בכותרת מחליף.
 */
function useVisitorLanguage(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    let stored: string | null = null;
    try {
      stored = localStorage.getItem('craftmind.locale');
    } catch {
      /* חסום — נשארים עם ברירת המחדל */
    }
    if (stored || navigator.language.toLowerCase().startsWith('he')) return;
    // אחרי שכל ה-effects של העץ רצו: useTranslation של הדף (ההורה) נרשם
    // לשינוי שפה רק ב-effect שלו, שרץ אחרי זה של ה-shell — בלי ההשהיה הוא
    // מפספס את האירוע, והטקסט נשאר בעברית בכיוון LTR.
    const id = setTimeout(() => setLocale('en', { reload: false }), 0);
    return () => clearTimeout(id);
  }, [enabled]);
}

/**
 * הכותרת והתחתית המשותפות לכל הדפים הציבוריים. `visitorLanguage` — אנגלית
 * למבקר ראשון שהדפדפן שלו לא בעברית (דפי המסמכים בלבד, ראו למעלה).
 */
export function PublicShell({
  children,
  wide = false,
  visitorLanguage = false,
}: {
  children: ReactNode;
  wide?: boolean;
  visitorLanguage?: boolean;
}) {
  const { t } = useTranslation(['public', 'common']);
  useVisitorLanguage(visitorLanguage);
  const other = currentLocale() === 'he' ? 'en' : 'he';
  const width = wide ? 'max-w-6xl' : 'max-w-3xl';
  return (
    <div className="min-h-dvh bg-canvas">
      <header className="sticky top-0 z-10 border-b border-border bg-surface/90 backdrop-blur">
        <div className={`mx-auto flex ${width} items-center justify-between gap-4 px-5 py-3`}>
          <Link to="/welcome" aria-label={LEGAL.product}>
            <Logo />
          </Link>
          <nav className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-xs text-fg-muted">
            <Link to="/welcome" hash="pricing" className="hover:text-fg">
              {t('legal.nav.pricing')}
            </Link>
            <Link to="/about" className="hover:text-fg">
              {t('legal.nav.about')}
            </Link>
            <Link to="/privacy" className="hover:text-fg">
              {t('legal.nav.privacy')}
            </Link>
            <Link to="/terms" className="hover:text-fg">
              {t('legal.nav.terms')}
            </Link>
            {/* מעבר שפה במקום, בלי טעינה — הבודקים ב-Google צריכים את האנגלית. */}
            <button
              type="button"
              lang={other}
              aria-label={t('legal.nav.languageAria')}
              className="hover:text-fg"
              onClick={() => setLocale(other, { reload: false })}
            >
              {t(other === 'he' ? 'common:language.he' : 'common:language.en')}
            </button>
            <Link to="/login" search={{}} className="font-medium text-accent hover:underline">
              {t('legal.nav.signIn')}
            </Link>
            <Link
              to="/onboarding"
              className="inline-flex h-8 items-center rounded-(--radius-md) bg-accent px-3 font-medium text-fg-on-accent hover:opacity-90"
            >
              {t('legal.nav.getStarted')}
            </Link>
          </nav>
        </div>
      </header>
      {children}
      <footer className="border-t border-border">
        <div
          className={`mx-auto flex ${width} flex-wrap items-center justify-between gap-3 px-5 py-6 text-2xs text-fg-subtle`}
        >
          <span>
            © {new Date().getFullYear()} {LEGAL.operator} ·{' '}
            <a href={`mailto:${LEGAL.contactEmail}`} className="hover:underline" dir="ltr">
              {LEGAL.contactEmail}
            </a>
          </span>
          <span className="flex gap-4">
            <Link to="/about" className="hover:underline">
              {t('legal.nav.about')}
            </Link>
            <Link to="/privacy" className="hover:underline">
              {t('legal.nav.privacy')}
            </Link>
            <Link to="/terms" className="hover:underline">
              {t('legal.nav.terms')}
            </Link>
          </span>
        </div>
      </footer>
    </div>
  );
}

function DocPage({
  title,
  updated,
  children,
}: {
  title: string;
  updated?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation('public');
  return (
    <PublicShell visitorLanguage>
      <main className="mx-auto max-w-3xl space-y-8 px-5 py-10">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold text-fg">{title}</h1>
          {updated ? (
            <p className="text-xs text-fg-muted">
              {t('legal.lastUpdated', {
                date: currentLocale() === 'he' ? LEGAL.updated : LEGAL.updatedEn,
              })}
            </p>
          ) : null}
        </header>
        <div className="space-y-8 text-sm leading-relaxed text-fg">{children}</div>
      </main>
    </PublicShell>
  );
}

interface Section {
  title: string;
  body?: string[];
  items?: string[];
  /** פסקאות אחרי הרשימה. */
  after?: string[];
}

function Sections({ sections }: { sections: Section[] }) {
  return (
    <>
      {sections.map((s) => (
        <section key={s.title} className="space-y-2">
          <h2 className="text-base font-semibold text-fg">{s.title}</h2>
          {s.body?.map((p) => (
            <p key={p}>{rich(p)}</p>
          ))}
          {s.items ? (
            <ul className="list-disc space-y-1.5 ps-5">
              {s.items.map((i) => (
                <li key={i}>{rich(i)}</li>
              ))}
            </ul>
          ) : null}
          {s.after?.map((p) => (
            <p key={p}>{rich(p)}</p>
          ))}
        </section>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------

export function AboutPage() {
  const { t } = useTranslation('public');
  const sections = t('legal.about.sections', { returnObjects: true, ...VALUES }) as Section[];
  return (
    <DocPage title={t('legal.about.title', VALUES)}>
      <p className="text-base text-fg-muted">{t('legal.about.intro', VALUES)}</p>
      <Sections sections={sections} />
      <section className="space-y-2">
        <h2 className="text-base font-semibold text-fg">{t('legal.about.whyTitle')}</h2>
        <ul className="list-disc space-y-1.5 ps-5">
          {(['signIn', 'sendEmail', 'readEmail', 'drive'] as const).map((k) => (
            <li key={k}>{rich(t(`legal.about.${k}`))}</li>
          ))}
        </ul>
        <p>{rich(t('legal.about.optional'))}</p>
      </section>
      <section className="space-y-2">
        <h2 className="text-base font-semibold text-fg">{t('legal.about.contactTitle')}</h2>
        <p>{rich(t('legal.about.contactBody', VALUES))}</p>
      </section>
      <div className="pt-2">
        <Link
          to="/onboarding"
          className="inline-flex h-10 items-center rounded-(--radius-md) bg-accent px-5 text-sm font-medium text-fg-on-accent hover:opacity-90"
        >
          {t('legal.about.cta')}
        </Link>
      </div>
    </DocPage>
  );
}

export function PrivacyPage() {
  const { t } = useTranslation('public');
  return (
    <DocPage title={t('legal.privacy.title')} updated>
      <Sections sections={t('legal.privacy.sections', { returnObjects: true, ...VALUES })} />
    </DocPage>
  );
}

export function TermsPage() {
  const { t } = useTranslation('public');
  return (
    <DocPage title={t('legal.terms.title')} updated>
      <Sections sections={t('legal.terms.sections', { returnObjects: true, ...VALUES })} />
    </DocPage>
  );
}
